'use client';

// A single managed-agent card: identity, mode + schedule, live spend meter,
// last-run + latest-outcome, and the action row (Wake / Pause-Resume / Open
// transcript / Settings / Archive). Runs feed expands inline.

import { useState } from 'react';
import Link from 'next/link';
import {
  ArrowsClockwise,
  ArrowSquareOut,
  CaretDown,
  Gear,
  Archive,
  Pause,
  Play,
  Clock,
  CaretRight,
  Lightning,
  Flask,
  TelegramLogo,
} from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { humanizeCron } from '@/lib/cron-humanize';
import type { AgentSummary } from './types';
import { relativeTime } from './relative-time';
import { AgentRunsFeed } from './agent-runs-feed';

const MODE_META: Record<AgentSummary['mode'], { label: string; className: string }> = {
  monitor: { label: 'Monitor', className: 'bg-sky-500/12 text-sky-400' },
  digest: { label: 'Digest', className: 'bg-violet-500/12 text-violet-400' },
  propose: { label: 'Propose', className: 'bg-emerald-500/12 text-emerald-400' },
};

const STATUS_META: Record<
  Exclude<AgentSummary['status'], 'archived'>,
  { label: string; dot: string; text: string }
> = {
  active: { label: 'Active', dot: 'bg-emerald-400', text: 'text-emerald-400' },
  paused: { label: 'Paused', dot: 'bg-muted-foreground/60', text: 'text-muted-foreground' },
  budget_paused: { label: 'Budget reached', dot: 'bg-amber-400', text: 'text-amber-400' },
};

function AgentAvatar({ agent }: { agent: AgentSummary }) {
  const color = agent.color || '#8B5CF6';
  return (
    <div
      className="grid size-10 shrink-0 place-items-center rounded-xl text-lg"
      style={{ backgroundColor: `${color}22`, color }}
    >
      {agent.emoji || '🤖'}
    </div>
  );
}

export function AgentCard({
  agent,
  busy,
  onWake,
  onTestRun,
  onTogglePause,
  onEdit,
  onArchive,
  onManageTriggers,
  onManageTelegram,
}: {
  agent: AgentSummary;
  busy: boolean;
  onWake: (agent: AgentSummary) => void;
  onTestRun: (agent: AgentSummary) => void;
  onTogglePause: (agent: AgentSummary) => void;
  onEdit: (agent: AgentSummary) => void;
  onArchive: (agent: AgentSummary) => void;
  onManageTriggers: (agent: AgentSummary) => void;
  onManageTelegram: (agent: AgentSummary) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const mode = MODE_META[agent.mode];
  const status = STATUS_META[agent.status as keyof typeof STATUS_META] ?? STATUS_META.active;
  const paused = agent.status === 'paused' || agent.status === 'budget_paused';

  const allTriggers = agent.eventTriggers ?? [];
  const activeTriggers = allTriggers.filter((t) => t.active);
  const triggerSources = [...new Set(activeTriggers.map((t) => t.source))].join(', ');

  const budget = Math.max(agent.dailyBudgetCredits, 1);
  const spent = Math.max(agent.todaySpend, 0);
  const spendPct = Math.min(100, (spent / budget) * 100);
  const overBudget = spent >= budget;

  return (
    <article className="flex flex-col gap-3.5 rounded-2xl border border-border/60 bg-card/60 p-[18px] transition-colors hover:border-border">
      {/* Identity + status */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <AgentAvatar agent={agent} />
          <div className="min-w-0">
            <p className="truncate text-[15.5px] font-extrabold tracking-tight" title={agent.name}>
              {agent.name}
            </p>
            <div className="mt-0.5 flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${mode.className}`}>
                {mode.label}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1 text-[11.5px] text-muted-foreground">
                    <Clock weight="duotone" className="size-3" />
                    {humanizeCron(agent.schedule)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {agent.schedule ? `${agent.schedule} · ${agent.timezone}` : 'No schedule — manual wake only'}
                </TooltipContent>
              </Tooltip>
              {allTriggers.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onManageTriggers(agent)}
                      className="flex items-center gap-1 rounded-md px-1 py-0.5 text-[11.5px] font-medium text-amber-400/90 transition-colors hover:bg-amber-400/10 hover:text-amber-300"
                    >
                      <Lightning weight="duotone" className="size-3" />
                      {activeTriggers.length}/{allTriggers.length} trigger{allTriggers.length > 1 ? 's' : ''}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Manage triggers{activeTriggers.length > 0 ? ` · reacts to ${triggerSources}` : ''}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <span className={`flex shrink-0 items-center gap-1.5 text-[11px] font-semibold ${status.text}`}>
          <span className={`size-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </div>

      {/* Spend meter */}
      <div>
        <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground/80">
          <span>Today's spend</span>
          <span className={overBudget ? 'font-semibold text-amber-400' : ''}>
            {spent} / {budget} cr
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full transition-all ${overBudget ? 'bg-amber-400' : 'bg-gradient-to-r from-[#3B5BD9] via-[#8B5CF6] to-[#06B6D4]'}`}
            style={{ width: `${spendPct}%` }}
          />
        </div>
      </div>

      {/* Last run + latest outcome */}
      <div className="flex items-center justify-between text-[11.5px] text-muted-foreground">
        <span>
          Last run {relativeTime(agent.lastRunAt)}
          {agent.configVersion > 1 && (
            <span className="ml-1.5 text-muted-foreground/60" title="Config version — bumps each time you edit the agent">
              · config v{agent.configVersion}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-muted-foreground transition-colors hover:text-foreground"
          aria-expanded={expanded}
        >
          {expanded ? <CaretDown weight="bold" className="size-3" /> : <CaretRight weight="bold" className="size-3" />}
          Activity
        </button>
      </div>

      {expanded && (
        <div className="rounded-xl border border-border/50 bg-background/40 p-1.5">
          <AgentRunsFeed agentId={agent.id} sessionId={agent.sessionId} />
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto flex items-center gap-1.5 pt-1">
        <button
          type="button"
          onClick={() => onWake(agent)}
          disabled={busy}
          className="flex items-center gap-1.5 rounded-full bg-violet-500 px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-violet-400 disabled:opacity-50"
        >
          <ArrowsClockwise weight="bold" className={`size-3.5 ${busy ? 'animate-spin' : ''}`} />
          Wake now
        </button>
        <IconAction label="Test run" onClick={() => onTestRun(agent)} disabled={busy}>
          <Flask weight="bold" className="size-3.5" />
        </IconAction>
        <IconAction
          label={paused ? 'Resume' : 'Pause'}
          onClick={() => onTogglePause(agent)}
          disabled={busy}
        >
          {paused ? <Play weight="fill" className="size-3.5" /> : <Pause weight="fill" className="size-3.5" />}
        </IconAction>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              href={`/chat/${agent.sessionId}`}
              className="grid size-8 place-items-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-border hover:text-foreground"
              aria-label="Open transcript"
            >
              <ArrowSquareOut weight="bold" className="size-3.5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="bottom">Open transcript</TooltipContent>
        </Tooltip>
        <IconAction label="Triggers" onClick={() => onManageTriggers(agent)}>
          <Lightning weight="bold" className="size-3.5" />
        </IconAction>
        <IconAction label="Telegram bot" onClick={() => onManageTelegram(agent)}>
          <TelegramLogo weight="bold" className="size-3.5" />
        </IconAction>
        <IconAction label="Settings" onClick={() => onEdit(agent)}>
          <Gear weight="bold" className="size-3.5" />
        </IconAction>
        <IconAction label="Archive" onClick={() => onArchive(agent)} className="ml-auto hover:text-rose-400">
          <Archive weight="bold" className="size-3.5" />
        </IconAction>
      </div>
    </article>
  );
}

function IconAction({
  label,
  onClick,
  disabled,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className={`grid size-8 place-items-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-border hover:text-foreground disabled:opacity-50 ${className ?? ''}`}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}
